// src/hooks/useCloudBackup.ts
import { useState, useEffect } from 'react';
import { auth, db, googleProvider } from '../config/firebase';
import { signInWithPopup, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { notifications } from '@mantine/notifications';
import { Bill } from '../types';

export interface UseCloudBackupReturn {
	loading: boolean;
	signInWithGoogle: () => Promise<User>;
	signOutUser: () => Promise<void>;
	saveToCloud: (bills: Bill[]) => Promise<void>;
	restoreFromCloud: () => Promise<Bill[]>;
	user: User | null;
	lastBackupDate: Date | null;
}

export function useCloudBackup(): UseCloudBackupReturn {
	const [loading, setLoading] = useState(false);
	const [currentUser, setCurrentUser] = useState<User | null>(auth.currentUser);
	const [lastBackupDate, setLastBackupDate] = useState<Date | null>(null);

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, (user) => {
			setCurrentUser(user);
			if (user) {
				// Fetch last backup date when user signs in
				fetchLastBackupDate(user.uid);
			} else {
				setLastBackupDate(null);
			}
		});

		return () => unsubscribe();
	}, []);

	const fetchLastBackupDate = async (userId: string): Promise<void> => {
		try {
			const userBillsRef = doc(db, 'userBills', userId);
			const docSnap = await getDoc(userBillsRef);

			if (docSnap.exists()) {
				const data = docSnap.data();
				if (data.lastUpdated) {
					setLastBackupDate(new Date(data.lastUpdated));
				}
			}
		} catch (error) {
			console.error('Error fetching last backup date:', error);
		}
	};

	const signInWithGoogle = async (): Promise<User> => {
		try {
			setLoading(true);
			const result = await signInWithPopup(auth, googleProvider);
			notifications.show({
				title: 'Successfully signed in',
				message: 'You can now use cloud backup',
				color: 'green',
			});
			return result.user;
		} catch (error) {
			console.error('Sign in error:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error';
			notifications.show({
				title: 'Sign in failed',
				message: errorMessage,
				color: 'red',
			});
			throw error;
		} finally {
			setLoading(false);
		}
	};

	const signOutUser = async (): Promise<void> => {
		try {
			await signOut(auth);
			notifications.show({
				title: 'Signed out',
				message: 'You have been signed out successfully',
				color: 'blue',
			});
		} catch (error) {
			console.error('Sign out error:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error';
			notifications.show({
				title: 'Sign out failed',
				message: errorMessage,
				color: 'red',
			});
		}
	};

	const saveToCloud = async (bills: Bill[]): Promise<void> => {
		if (!currentUser) {
			throw new Error('Must be signed in to use cloud backup');
		}

		try {
			setLoading(true);
			const userBillsRef = doc(db, 'userBills', currentUser.uid);
			const now = new Date();

			const backupData = {
				bills: bills,
				lastUpdated: now.toISOString(),
				userId: currentUser.uid,
				userEmail: currentUser.email,
			};

			await setDoc(userBillsRef, backupData, { merge: true });
			setLastBackupDate(now);

			notifications.show({
				title: 'Backup successful',
				message: 'Your bills have been backed up to the cloud',
				color: 'green',
			});
		} catch (error) {
			console.error('Cloud backup error:', error);
			notifications.show({
				title: 'Backup failed',
				message:
					'Failed to backup data to the cloud. Please try again.',
				color: 'red',
			});
			throw error;
		} finally {
			setLoading(false);
		}
	};

	const restoreFromCloud = async (): Promise<Bill[]> => {
		if (!currentUser) {
			throw new Error('Must be signed in to restore from cloud');
		}

		try {
			setLoading(true);
			const userBillsRef = doc(db, 'userBills', currentUser.uid);
			const docSnap = await getDoc(userBillsRef);

			if (docSnap.exists()) {
				const data = docSnap.data();
				if (data && data.bills) {
					if (data.lastUpdated) {
						setLastBackupDate(new Date(data.lastUpdated));
					}
					notifications.show({
						title: 'Restore successful',
						message: 'Your bills have been restored from the cloud',
						color: 'green',
					});
					return data.bills as Bill[];
				} else {
					throw new Error('No bills found in the backup');
				}
			} else {
				throw new Error('No backup found in the cloud');
			}
		} catch (error) {
			console.error('Cloud restore error:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error';
			notifications.show({
				title: 'Restore failed',
				message: errorMessage,
				color: 'red',
			});
			throw error;
		} finally {
			setLoading(false);
		}
	};

	return {
		loading,
		signInWithGoogle,
		signOutUser,
		saveToCloud,
		restoreFromCloud,
		user: currentUser,
		lastBackupDate,
	};
}
